import { Card, Table } from "antd";
import ListTable from "common/ListTable";
import moment from "moment";
import { useMemo } from "react";
const formatIntWithCommas = (value) => {
  return parseInt(value)?.toLocaleString();
};

const formatFloatWithCommas = (value) => {
  return parseFloat(value).toFixed(2).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const columnsSharingMerchandisers = [
  {
    title: "Dealing Merchandiser",
    dataIndex: "dealing_merchandiser",
    key: "dealing_merchandiser",
  },
  {
    title: "Buyer Name",
    dataIndex: "buyer",
    key: "buyer",
  },
  {
    title: "Swing Qty",
    dataIndex: "swing_qty",
    key: "swing_qty",
    align: "right",
    render: formatIntWithCommas,
  },
  {
    title: "Avg CM/USD",
    dataIndex: "cm",
    key: "cm",
    align: "right",
    render: formatFloatWithCommas,
  },
  {
    title: "Income CM/USD",
    dataIndex: "income_cm_usd",
    key: "income_cm_usd",
    align: "right",
    render: formatFloatWithCommas,
  },
  {
    title: "Income CM/Taka",
    dataIndex: "income_cm_taka",
    key: "income_cm_taka",
    align: "right",
    render: formatIntWithCommas,
  },
  {
    title: "Expenditure Taka",
    dataIndex: "expenses_taka",
    key: "expenses_taka",
    align: "right",
    render: formatIntWithCommas,
  },
  {
    title: "Up-to-date Est. Net Profit/Taka",
    dataIndex: "today_net_profit",
    key: "today_net_profit",
    align: "right",
    render: (value) => {
      const numValue = parseFloat(value) || 0;
      return (
        <span style={{ color: numValue < 0 ? "red" : "green" }}>
          {formatIntWithCommas(Math.abs(numValue))}
          {numValue < 0 && " (Loss)"}
        </span>
      );
    },
  },
  {
    title: "Cumulative Projected Profit/(Loss) Taka",
    dataIndex: "today_projected_profit",
    key: "today_projected_profit",
    align: "right",
    render: (value) => value || "-",
  },
  {
    title: "Variance-Profit/Taka",
    dataIndex: "variance_profit_taka",
    key: "variance_profit_taka",
    align: "right",
    render: (value) => value || "-",
  },
  {
    title: "% of NP on Total Day/Profit",
    dataIndex: "np_percentage",
    key: "np_percentage",
    align: "right",
    render: (value) => (value ? `${parseFloat(value).toFixed(2)}%` : "-"),
  },
];

const CumulativeProfitLossStatement = ({
  upToDateData,
  loadingUpToDateData,
  startDateWatch,
  endDateWatch,
}) => {
  // Prepare data from API response
  const dataSource = useMemo(() => {
    if (!upToDateData || !Array.isArray(upToDateData)) {
      console.log("No cumulative data available or data is not an array");
      return [];
    }

    return upToDateData.map((item, index) => ({
      key: item.id || index,
      ...item,
    }));
  }, [upToDateData]);

  console.log("Cumulative data source:", dataSource);

  return (
    <Card
      title={`Cumulative Profit & (Loss) Statement: ${
        startDateWatch && endDateWatch
          ? `(${moment(startDateWatch).format("DD-MMM-YY")} to ${moment(
              endDateWatch,
            ).format("DD-MMM-YY")})`
          : ""
      }`}
      bordered={false}
      style={{
        boxShadow: "none",
      }}
    >
      <ListTable
        dataSource={dataSource}
        columns={columnsSharingMerchandisers}
        pagination={false}
        bordered
        loading={loadingUpToDateData}
        emptyText="No records to show yet."
        title={() => "Sharing of above All by Merchandisers: "}
        footer={() => (
          <>
            <p>
              The expenses has been Allocated on pc based on % of CM on total
              earned CM.
            </p>
            <p>
              The performance has been calculated in combination of three
              garment units day production basis.
            </p>
          </>
        )}
        summary={(data) => {
          let totalSwingQty = 0;
          let totalCm = 0;
          let totalIncomeCmUsd = 0;
          let totalIncomeCmTaka = 0;
          let totalExpensesTaka = 0;
          let totalCumulativeNetProfit = 0;
          let totalProjectedProfit = 0;
          let totalVarianceProfit = 0;
          let totalNpPercentage = 0;
          let validItemsCount = 0;

          data.forEach((item) => {
            const swingQty = parseFloat(item.swing_qty) || 0;
            const cm = parseFloat(item.cm) || 0;
            const incomeCmUsd = parseFloat(item.income_cm_usd) || 0;
            const incomeCmTaka = parseFloat(item.income_cm_taka) || 0;
            const expensesTaka = parseFloat(item.expenses_taka) || 0;
            const cumulativeNetProfit = parseFloat(item.today_net_profit) || 0;
            const projectedProfit =
              parseFloat(item.today_projected_profit) || 0;
            const varianceProfit = parseFloat(item.variance_profit_taka) || 0;
            const npPercentage = parseFloat(item.np_percentage) || 0;

            totalSwingQty += swingQty;
            totalCm += cm;
            totalIncomeCmUsd += incomeCmUsd;
            totalIncomeCmTaka += incomeCmTaka;
            totalExpensesTaka += expensesTaka;
            totalCumulativeNetProfit += cumulativeNetProfit;
            totalProjectedProfit += projectedProfit;
            totalVarianceProfit += varianceProfit;
            totalNpPercentage += npPercentage;
            validItemsCount++;
          });

          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell />
                <Table.Summary.Cell align="right">
                  <strong>{formatIntWithCommas(totalSwingQty)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>
                    {validItemsCount > 0
                      ? formatFloatWithCommas(totalCm / validItemsCount)
                      : "0.00"}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>{formatFloatWithCommas(totalIncomeCmUsd)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>{formatIntWithCommas(totalIncomeCmTaka)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>{formatIntWithCommas(totalExpensesTaka)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong
                    style={{
                      color: totalCumulativeNetProfit < 0 ? "red" : "green",
                    }}
                  >
                    {formatIntWithCommas(Math.abs(totalCumulativeNetProfit))}
                    {totalCumulativeNetProfit < 0 && " (Loss)"}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>
                    {totalProjectedProfit
                      ? formatIntWithCommas(totalProjectedProfit)
                      : "-"}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>
                    {totalVarianceProfit
                      ? formatIntWithCommas(totalVarianceProfit)
                      : "-"}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>
                    {validItemsCount > 0
                      ? `${(totalNpPercentage / validItemsCount).toFixed(2)}%`
                      : "-"}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
    </Card>
  );
};

export default CumulativeProfitLossStatement;
